import { authService } from '@/lib/auth'
import { env } from '@/config/env'
import type { PortalSessionResponse, UserUsageData } from './types'

export type { UserUsageData }

export const BASE_URL = env.apiEndpoint // lambda function url

// Custom error class for rate limit (429) errors
export class RateLimitError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RateLimitError'
  }
}

// Helper to check if a message indicates a rate limit error
const isRateLimitMessage = (message: string): boolean => {
  const lowerMessage = message.toLowerCase()
  return (
    lowerMessage.includes('limit exceeded') ||
    lowerMessage.includes('usage limit')
  )
}

export const useMock = env.isDev

export const EventSourceMessageTypes = {
  STATUS: 'status',
  COMPLETE: 'complete',
  FRAME: 'frame',
}

export enum ConversationStep {
  INITIAL_ANALYSIS = 'initial_analysis',
  GOALS_COLLECTED = 'goals_collected',
  ENHANCEMENTS_SUGGESTED = 'enhancements_suggested',
  IMAGE_EDITED = 'image_edited',
}

export enum ResponseType {
  EXPLANATION_WITH_QUESTION = 'explanation_with_question',
  ENHANCEMENT_SUGGESTIONS = 'enhancement_suggestions',
  EDITED_IMAGE = 'edited_image',
}

export interface ConversationResult {
  run_id: string
  response: string
  timestamp?: string
  conversation_step?: ConversationStep
  response_type?: ResponseType
  conversation_id?: string
}

// Helper function to create authenticated headers
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await authService.getAccessToken()
  const headers: HeadersInit = {}

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

// Authenticated fetch wrapper
const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const authHeaders = await getAuthHeaders()

  const mergedOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers || {}),
    },
  }

  const response = await fetch(url, mergedOptions)

  // Handle 401 responses
  if (response.status === 401) {
    throw new Error('Authentication failed - please sign in again')
  }

  // Handle 429 rate limit responses or responses with rate limit messages
  if (response.status === 429 || !response.ok) {
    const defaultMessage =
      'You have reached your usage limit. Please upgrade your plan to continue.'

    // Clone response so we can read the body and still return the response if not a rate limit error
    const clonedResponse = response.clone()

    try {
      const data = await clonedResponse.json()
      const errorMessage = data.message || data.error || ''

      // Check if it's a 429 or if the message contains rate limit keywords
      if (response.status === 429 || isRateLimitMessage(errorMessage)) {
        throw new RateLimitError(errorMessage || defaultMessage)
      }
    } catch (e) {
      // If it's already a RateLimitError, rethrow it
      if (e instanceof RateLimitError) {
        throw e
      }
      // For 429 with unparseable body, still throw rate limit error
      if (response.status === 429) {
        throw new RateLimitError(defaultMessage)
      }
    }
  }

  return response
}

// Ping endpoint (no authentication required - used for both warmup and health checks)
export const ping = async () => {
  const response = await fetch(`${BASE_URL}/ping`)
  if (!response.ok) {
    throw new Error('Failed to ping')
  }
  const data = await response.json()
  if (data.body) {
    return JSON.parse(data.body)
  }
  return data
}

export const getS3UploadUrl = async (fileName: string, mimeType: string) => {
  try {
    const response = await authenticatedFetch(`${BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_name: fileName,
        file_type: mimeType,
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to upload file: ${response.status} ${response.statusText}`,
      )
    }

    const data = await response.json()
    if (data.body) {
      return JSON.parse(data.body)
    }
    return data
  } catch (e) {
    console.error(e)
    throw new Error('Failed to upload file')
  }
}

// Generic streaming function that handles SSE wrapped in JSON with authentication
const processGenericStream = async (
  streamUrl: string,
  onMessage: (data: any) => boolean | void,
  onError: (error?: Error) => void,
) => {
  console.log('Connecting to authenticated stream:', streamUrl)

  let isConnectionClosed = false

  try {
    const response = await authenticatedFetch(streamUrl, {
      method: 'GET',
      headers: {
        Accept: 'text/plain',
        'Cache-Control': 'no-cache',
      },
    })

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`,
      )
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get stream reader')
    }

    const decoder = new TextDecoder()
    let accumulatedBuffer = ''

    const processStream = async () => {
      try {
        while (!isConnectionClosed) {
          const { done, value } = await reader.read()

          if (done) {
            console.log('Stream completed')
            // Process the complete accumulated buffer
            if (accumulatedBuffer.trim()) {
              try {
                processSSEBody(accumulatedBuffer)
              } catch (jsonError) {
                console.error(
                  'Error parsing JSON response:',
                  jsonError,
                  'Buffer:',
                  accumulatedBuffer,
                )
              }
            }
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          accumulatedBuffer += chunk

          // Just accumulate - parse only when stream is complete
        }
      } catch (streamError) {
        console.error('Stream error:', streamError)
        if (!isConnectionClosed) {
          isConnectionClosed = true
          onError(streamError instanceof Error ? streamError : undefined)
        }
      } finally {
        reader.releaseLock()
      }
    }

    const processSSEBody = (sseBody: string) => {
      // Parse the SSE format from the response body
      if (sseBody && sseBody.includes('data: ')) {
        const lines = sseBody.split('\n')

        for (const line of lines) {
          if (line.trim() === '' || isConnectionClosed) continue

          // Handle SSE format: look for data: lines
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.substring(6) // Remove 'data: ' prefix
              const data = JSON.parse(jsonData)
              console.log('Received stream message:', data)

              // Let the caller handle the message
              const shouldClose = onMessage(data)
              if (shouldClose) {
                isConnectionClosed = true
                return
              }
            } catch (parseError) {
              console.error(
                'Error parsing stream data:',
                parseError,
                'Line:',
                line,
              )
            }
          }
          // Skip event: lines and other non-data lines
        }
      }
    }

    processStream()
  } catch (fetchError) {
    console.error('Fetch error:', fetchError)
    if (!isConnectionClosed) {
      isConnectionClosed = true
      onError(fetchError instanceof Error ? fetchError : undefined)
    }
  }

  return () => {
    if (!isConnectionClosed) {
      isConnectionClosed = true
    }
  }
}

export const getAnalysisRuns = async () => {
  const response = await authenticatedFetch(`${BASE_URL}/runs`)
  if (!response.ok) {
    throw new Error(
      `Failed to get analysis runs: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getAnalysisRun = async (runId: string) => {
  const response = await authenticatedFetch(`${BASE_URL}/runs/${runId}`)
  if (!response.ok) {
    throw new Error(
      `Failed to get analysis run: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const updateAnalysisRun = async (runId: string, data: any) => {
  const response = await authenticatedFetch(`${BASE_URL}/runs/${runId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.json()
}

export const processImageStream = async (
  s3Uri: string,
  onStatus: (progress: number, message: string) => void,
  onComplete: (prediction: any) => void,
  onError: (error?: Error) => void,
) => {
  const streamUrl = `${BASE_URL}/predict_image/stream?s3_uri=${encodeURIComponent(s3Uri)}`

  return processGenericStream(
    streamUrl,
    data => {
      switch (data.type) {
        case EventSourceMessageTypes.STATUS:
          onStatus(data.progress, data.message)
          break
        case EventSourceMessageTypes.COMPLETE:
          onComplete(data.prediction)
          return true // Signal to close connection
        default:
          console.log(data)
          break
      }
      return false // Continue processing
    },
    onError,
  )
}

export const processVideoStream = async (
  s3Uri: string,
  onStatus: (progress: number, message: string) => void,
  onFrame: (prediction: any, frameIndex: number) => void,
  onComplete: () => void,
  onError: (error?: Error) => void,
) => {
  const streamUrl = `${BASE_URL}/predict_video/stream?s3_uri=${encodeURIComponent(s3Uri)}`

  return processGenericStream(
    streamUrl,
    data => {
      switch (data.type) {
        case EventSourceMessageTypes.STATUS:
          onStatus(data.progress ?? 0, data.message)
          break
        case EventSourceMessageTypes.FRAME:
          onFrame(data.prediction, data.frame_index)
          onStatus(
            data.progress ?? 0,
            `Processing ${data.frame_index + 1} of ${data.total_frames} frames`,
          )
          break
        case EventSourceMessageTypes.COMPLETE:
          onComplete()
          return true // Signal to close connection
        default:
          console.log(data)
          break
      }
      return false // Continue processing
    },
    onError,
  )
}

export const getImageAnalysisExplanations = async (
  runId: string,
): Promise<ConversationResult> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/start`,
    {
      method: 'POST',
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get image analysis explanations: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getImageEnhancementSuggestions = async (
  runId: string,
  conversationId: string,
  userResponse: string,
): Promise<ConversationResult> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/continue`,
    {
      method: 'POST',
      body: JSON.stringify({
        run_id: runId,
        conversation_id: conversationId,
        user_response: userResponse,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get image enhancement suggestions: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const generateEnhancedImage = async (
  runId: string,
  conversationId: string,
  imageUri: string,
) => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/${runId}/multi-turn/edit-image`,
    {
      method: 'POST',
      body: JSON.stringify({
        run_id: runId,
        conversation_id: conversationId,
        image_url: imageUri,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to generate enhanced image: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getUserUsage = async (): Promise<UserUsageData> => {
  const response = await authenticatedFetch(`${BASE_URL}/usage`)
  if (!response.ok) {
    throw new Error(
      `Failed to get user usage: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getStripeCustomerPortalUrl =
  async (): Promise<PortalSessionResponse> => {
    const response = await authenticatedFetch(
      `${BASE_URL}/create-portal-session?frontend_url=${window.location.origin}`,
    )
    if (!response.ok) {
      throw new Error(
        `Failed to get stripe customer portal url: ${response.status} ${response.statusText}`,
      )
    }
    return response.json()
  }

// Showcase/Demo API functions (no authentication required)

export interface ShowcaseRun {
  userName: string
  actionTimestamp: string
  s3Uri?: string
  sourceS3Uri?: string
  uploadTimestamp?: number
  uploadTimestampStr?: string
  status?: string
  userEmail?: string
  fileName?: string
  fileType?: string
  runId?: string
  runName?: string
  analysisVersion?: number
  totalFramesProcessed?: number
  totalFrames?: number
  fps?: number
  sizeBytes?: number
  originalUrl?: string
  score?: number
}

export interface ShowcaseRunsResponse {
  runs: ShowcaseRun[]
  count: number
}

export interface HotspotData {
  x_pct: number
  y_pct: number
  intensity: number
}

export interface ShowcasePredictionFrameData {
  video_url?: string
  frame_timestamp?: number
  emotion_valence?: number
  emotion_arousal?: number
  prediction_url: string
  prediction_s3_uri: string
  explanation?: string
  explanation_v3?: string
  original_url?: string
  hotspots?: HotspotData[]
}

export interface ShowcasePredictionRunData {
  run_id: string
  user_name: string
  file_type: 'image' | 'video'
  predictions: ShowcasePredictionFrameData | ShowcasePredictionFrameData[]
}

export interface ShowcaseRunResponse {
  run: ShowcaseRun | null
  predictions: ShowcasePredictionRunData | null
}

export const getShowcaseRuns = async (): Promise<ShowcaseRunsResponse> => {
  const response = await fetch(`${BASE_URL}/showcase/runs`)
  if (!response.ok) {
    throw new Error(
      `Failed to get showcase runs: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

export const getShowcaseRun = async (
  runId: string,
): Promise<ShowcaseRunResponse> => {
  const response = await fetch(`${BASE_URL}/showcase/runs/${runId}`)
  if (!response.ok) {
    throw new Error(
      `Failed to get showcase run: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

// V3 Analysis Types and API

export interface RiskFix {
  title: string
  severity: 'warning' | 'critical'
  fixes: string[]
  score_impact: number
}

export interface VerdictTag {
  label: string
  confidence: number
}

export interface AnalysisV3Verdict {
  headline: string
  best_for: VerdictTag[]
  avoid_for: VerdictTag[]
}

export interface AnalysisV3DeepAnalysis {
  emotional_breakdown?: string
  scene_interpretation?: string
  copy_text_analysis?: string
  competitive_benchmark?: string
}

export interface AnalysisV3Annotation {
  x_pct: number
  y_pct: number
  label: string
  type: 'drives_clicks' | 'hurts_ctr' | 'neutral'
  detail: string
  risk_index?: number | null
}

// Qualitative creative-quality signals. We don't have a deterministic CTR/ROAS
// model, so the LLM emits one of "Strong" | "Moderate" | "Weak" instead of
// fabricated percentages. Legacy `predicted_roas_range` and `projected_ctr`
// are kept optional so cached runs created before this change still parse.
export type CreativeTier = 'Strong' | 'Moderate' | 'Weak'

export interface AnalysisV3Result {
  analysis_version?: number
  score: number
  confidence: string
  confidence_reason?: string
  benchmark_percentile?: number
  attention_pull?: CreativeTier | string
  audience_resonance?: CreativeTier | string
  predicted_roas_range?: string
  projected_ctr?: string
  signals: {
    scroll_stopping_power?: number
    brand_recall?: number
    attention_strength?: number
    brand_visibility?: number
  }
  risk_fixes?: RiskFix[]
  verdict: AnalysisV3Verdict
  deep_analysis?: AnalysisV3DeepAnalysis
  annotations?: AnalysisV3Annotation[]
}

export interface AnalysisV3Response {
  conversation_id: string
  response: string
  response_type: string
  conversation_history: any[]
  current_step: string
}

export const getAnalysisV3 = async (
  runId: string,
): Promise<AnalysisV3Response> => {
  const response = await authenticatedFetch(
    `${BASE_URL}/runs/v2/${runId}/explain`,
  )
  if (!response.ok) {
    throw new Error(
      `Failed to get v3 analysis: ${response.status} ${response.statusText}`,
    )
  }
  return response.json()
}

// Remap legacy risks/recommendations fields into risk_fixes for cached runs
const normalizeLegacyFields = (raw: any): AnalysisV3Result => {
  if (!raw.risk_fixes && (raw.risks || raw.recommendations)) {
    const risks: string[] = raw.risks ?? []
    const recommendations: Array<{ text: string; reason: string }> =
      raw.recommendations ?? []
    const risk_fixes: RiskFix[] = risks.map((title, i) => ({
      title,
      severity: 'warning' as const,
      fixes: recommendations[i]?.text ? [recommendations[i].text] : [],
      score_impact: 5,
    }))
    raw = { ...raw, risk_fixes }
  }
  // Coerce legacy `fix: string` (or `fix: string[]`) into `fixes: string[]`
  if (Array.isArray(raw.risk_fixes)) {
    raw = {
      ...raw,
      risk_fixes: raw.risk_fixes.map((rf: any) => {
        if (Array.isArray(rf?.fixes) && rf.fixes.length > 0) return rf
        const legacy = rf?.fix
        let fixes: string[] = []
        if (Array.isArray(legacy)) {
          fixes = legacy.map((f: unknown) => String(f).trim()).filter(Boolean)
        } else if (typeof legacy === 'string' && legacy.trim()) {
          fixes = [legacy.trim()]
        }
        return { ...rf, fixes }
      }),
    }
  }
  // Remap legacy best_for/avoid_for string arrays to VerdictTag arrays
  if (raw.verdict) {
    if (
      Array.isArray(raw.verdict.best_for) &&
      raw.verdict.best_for.length > 0 &&
      typeof raw.verdict.best_for[0] === 'string'
    ) {
      raw = {
        ...raw,
        verdict: {
          ...raw.verdict,
          best_for: (raw.verdict.best_for as string[]).map(label => ({
            label,
            confidence: 80,
          })),
          avoid_for: (raw.verdict.avoid_for as string[]).map(label => ({
            label,
            confidence: 80,
          })),
        },
      }
    }
  }
  return raw as AnalysisV3Result
}

export const parseAnalysisV3Response = (
  response: AnalysisV3Response,
): AnalysisV3Result | null => {
  try {
    let jsonStr = response.response
    if (jsonStr.startsWith('```json')) {
      jsonStr = jsonStr.replace(/^```json\s*/, '').replace(/\s*```$/, '')
    }
    const raw = JSON.parse(jsonStr)
    return normalizeLegacyFields(raw)
  } catch (e) {
    console.error('Failed to parse v3 analysis response:', e)
    return null
  }
}
